import { router } from "@inertiajs/react";
import { ArrowRight, LoaderIcon, TriangleAlert } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getCsrfToken } from "@/lib/csrf";
import type {
	MergePreviewResult,
	PatientSearchCandidate,
} from "@/types/admin-portal/patient";
import { AsyncSearchCombobox } from "./async-search-combobox";

export interface MergePatientDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	keeperId: string;
	keeperName: string;
	keeperNumber?: string;
}

const jsonHeaders = () => ({
	"Content-Type": "application/json",
	Accept: "application/json",
	"X-CSRF-Token": getCsrfToken(),
});

export default function MergePatientDialog({
	open,
	onOpenChange,
	keeperId,
	keeperName,
	keeperNumber,
}: MergePatientDialogProps) {
	const { t } = useTranslation("patients", { keyPrefix: "merge" });
	const [source, setSource] = useState<PatientSearchCandidate | null>(null);
	const [reason, setReason] = useState("");
	const [preview, setPreview] = useState<MergePreviewResult | null>(null);
	const [previewing, setPreviewing] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [merging, setMerging] = useState(false);
	// Mirrors the currently selected source id (updated synchronously wherever
	// `source` changes) so an in-flight preview request can tell, once it
	// resolves, whether it is still answering the currently selected source.
	const currentSourceIdRef = useRef<string | null>(null);

	function reset() {
		setSource(null);
		setReason("");
		setPreview(null);
		setConfirmOpen(false);
		currentSourceIdRef.current = null;
	}

	async function onPreview() {
		if (!source) return;
		const requestedId = source.id;
		setPreviewing(true);
		setPreview(null);
		try {
			const res = await fetch(
				`/admin-portal/patient-management/${keeperId}/merge_preview`,
				{
					method: "POST",
					headers: jsonHeaders(),
					body: JSON.stringify({ source_patient_id: requestedId }),
				},
			);
			const data = await res.json();
			// Discard this response if the selected source changed while the
			// request was in flight (e.g. picked A, then B before A resolved).
			if (currentSourceIdRef.current !== requestedId) return;
			if (res.ok && data.success) {
				setPreview(data.data as MergePreviewResult);
			} else {
				toast.error(data.error || t("messages.error"));
			}
		} catch {
			if (currentSourceIdRef.current !== requestedId) return;
			toast.error(t("messages.error"));
		} finally {
			// Always clear the in-flight flag, even for a superseded request —
			// the newly selected source never started its own request, so
			// guarding this on a source match would leave the button stuck
			// disabled with a spinner that never resolves.
			setPreviewing(false);
		}
	}

	async function onMerge() {
		if (!source) return;
		setMerging(true);
		try {
			const res = await fetch(
				`/admin-portal/patient-management/${keeperId}/merge`,
				{
					method: "POST",
					headers: jsonHeaders(),
					body: JSON.stringify({
						source_patient_id: source.id,
						reason: reason || undefined,
					}),
				},
			);
			const data = await res.json();
			if (res.ok && data.success) {
				toast.success(data.message || t("messages.success"));
				onOpenChange(false);
				reset();
				router.reload();
			} else {
				toast.error(data.error || t("messages.error"));
			}
		} catch {
			toast.error(t("messages.error"));
		} finally {
			setMerging(false);
			setConfirmOpen(false);
		}
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(v) => {
				if (!v) reset();
				onOpenChange(v);
			}}
		>
			<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{t("title")}</DialogTitle>
					<DialogDescription>{t("description")}</DialogDescription>
				</DialogHeader>

				<div className="grid gap-4">
					<Alert>
						<AlertTitle>{t("explainer.title")}</AlertTitle>
						<AlertDescription>{t("explainer.body")}</AlertDescription>
					</Alert>

					{/* Merge direction: keeper survives, source is absorbed then deleted */}
					<div className="grid items-stretch gap-2 md:grid-cols-[1fr_auto_1fr]">
						<div className="grid gap-1 p-3 border rounded-md">
							<Badge variant="secondary" className="justify-self-start">
								{t("keeper_badge")}
							</Badge>
							<span className="text-sm font-medium">{keeperName}</span>
							<span className="text-xs text-muted-foreground">
								{keeperNumber || "-"}
							</span>
						</div>

						<div className="flex items-center justify-center">
							<ArrowRight className="size-4 rotate-90 text-muted-foreground md:rotate-0" />
						</div>

						<div className="grid gap-1 p-3 border rounded-md border-destructive/50">
							<Badge variant="destructive" className="justify-self-start">
								{t("source_badge")}
							</Badge>
							{source ? (
								<>
									<span className="text-sm font-medium">{source.name}</span>
									<span className="text-xs text-muted-foreground">
										{source.patientNumber || "-"}
									</span>
								</>
							) : (
								<span className="text-sm text-muted-foreground">
									{t("source_empty")}
								</span>
							)}
						</div>
					</div>

					<div className="grid gap-1.5">
						<Label>{t("source_label")}</Label>
						<AsyncSearchCombobox<PatientSearchCandidate>
							fetchUrl="/admin-portal/patient-management/search"
							extraParams={{ exclude_id: keeperId }}
							getKey={(p) => p.id}
							placeholder={t("source_placeholder")}
							emptyText={t("empty")}
							onSelect={(p) => {
								currentSourceIdRef.current = p.id;
								setSource(p);
								setPreview(null);
							}}
							renderItem={(p) => (
								<div className="flex flex-col">
									<span className="text-sm">{p.name}</span>
									<span className="text-xs text-muted-foreground">
										{p.patientNumber} · {p.gender}
									</span>
								</div>
							)}
						/>
					</div>

					<Button
						variant="secondary"
						onClick={onPreview}
						disabled={!source || previewing}
					>
						{previewing && <LoaderIcon className="mr-2 size-4 animate-spin" />}
						{t("preview_button")}
					</Button>

					{preview && (
						<div className="grid gap-2 p-3 text-sm border rounded-md">
							<p className="font-medium">{t("preview_title")}</p>
							<ul className="grid gap-0.5">
								{(preview.counts ?? []).map((c) => (
									<li key={c.table} className="flex justify-between">
										<span className="text-muted-foreground">{c.table}</span>
										<span className="tabular-nums">{c.count}</span>
									</li>
								))}
							</ul>
							{(preview.warnings ?? []).length > 0 && (
								<div className="mt-1">
									<p className="font-medium text-amber-600">
										{t("warnings_title")}
									</p>
									<ul className="pl-4 text-xs list-disc text-amber-600">
										{(preview.warnings ?? []).map((w) => (
											<li key={w}>{w}</li>
										))}
									</ul>
								</div>
							)}
						</div>
					)}

					{preview && (
						<Alert variant="destructive">
							<TriangleAlert className="size-4" />
							<AlertTitle>{t("caution.title")}</AlertTitle>
							<AlertDescription>{t("caution.body")}</AlertDescription>
						</Alert>
					)}

					<div className="grid gap-1.5">
						<Label htmlFor="merge-reason">{t("reason.label")}</Label>
						<Textarea
							id="merge-reason"
							rows={3}
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							placeholder={t("reason.placeholder")}
						/>
					</div>

					<div className="flex justify-end gap-2">
						<Button variant="outline" onClick={() => onOpenChange(false)}>
							{t("cancel")}
						</Button>
						<Button
							variant="destructive"
							disabled={!source || !preview}
							onClick={() => setConfirmOpen(true)}
						>
							{t("confirm")}
						</Button>
					</div>
				</div>
			</DialogContent>

			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("confirm_title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("confirm_body", {
								source: source?.name ?? "",
								keeper: keeperName,
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={merging}>
							{t("cancel")}
						</AlertDialogCancel>
						<AlertDialogAction onClick={onMerge} disabled={merging}>
							{merging && <LoaderIcon className="mr-2 size-4 animate-spin" />}
							{t("confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Dialog>
	);
}
